import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';

// ─── LinkedIn OAuth ───────────────────────────────────────────────────────────
// Supabase dashboard config required:
//   Authentication > Providers > LinkedIn (OIDC) > enable
//   Client ID + Client Secret from https://www.linkedin.com/developers/apps
//   Redirect URL to add in LinkedIn app: https://<SUPABASE_PROJECT_REF>.supabase.co/auth/v1/callback
//   Supabase > Authentication > URL Configuration:
//     Site URL: https://recruteurs.io (+ localhost:5173 for local dev)
//     Additional Redirect URLs: https://recruteurs.io/** + http://localhost:5173/**
// ─────────────────────────────────────────────────────────────────────────────

function LinkedInIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

export default function AuthModal({
  isOpen,
  onClose,
  next,
}: {
  isOpen: boolean;
  onClose: () => void;
  next?: string | null;
}) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'client' | 'candidat'>('candidat');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleAuth = async () => {
    setLoading(true);
    setErrorMsg(null);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              // Security: never send 'role' — the DB trigger handles it.
              // Only 'candidat' and 'client' are accepted by handle_new_user;
              // any admin/editor escalation attempt via metadata is blocked.
              user_type: role === 'client' ? 'client' : 'candidat',
            },
          },
        });

        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }

      // SSO: if we were opened via ?auth=open&next=..., redirect to next.
      if (next) {
        window.location.href = next;
        return;
      }
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setErrorMsg(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedIn = async () => {
    setOauthLoading(true);
    setErrorMsg(null);

    // Store current path so we can restore it after OAuth redirect
    try {
      localStorage.setItem('auth_return_url', window.location.pathname + window.location.search);
    } catch {
      // localStorage unavailable — proceed without storing
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          // Si SSO ?next=, on rebondit là après le callback OAuth.
          redirectTo: next || window.location.origin,
        },
      });

      if (error) throw error;
      // Browser will redirect to LinkedIn — no further action needed
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur de connexion LinkedIn';
      setErrorMsg(errorMessage);
      toast.error('La connexion LinkedIn a échoué. Réessayez.');
      setOauthLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setErrorMsg(null);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-04.png"
              alt="Recruteurs.io"
              className="h-12 mb-4"
            />
            <Dialog.Title className="text-xl font-semibold text-center">
              {isSignUp ? 'Créer un compte' : 'Connexion'}
            </Dialog.Title>
          </div>

          {/* LinkedIn OAuth */}
          <button
            type="button"
            onClick={handleLinkedIn}
            disabled={oauthLoading || loading}
            className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 hover:border-[#0077b5] hover:text-[#0077b5] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            <LinkedInIcon />
            {oauthLoading ? 'Redirection…' : 'Continuer avec LinkedIn'}
          </button>

          {/* Separator */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-neutral-100" />
            <span className="text-xs text-neutral-400 font-medium">ou</span>
            <div className="flex-1 h-px bg-neutral-100" />
          </div>

          {isSignUp && (
            <>
              <input
                className="w-full mb-3 p-2 border rounded"
                placeholder="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                className="w-full mb-3 p-2 border rounded"
                placeholder="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <div className="mb-3">
                <label htmlFor="role" className="block text-sm font-medium text-neutral-700 mb-1">
                  Vous êtes
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'client' | 'candidat')}
                  className="w-full border px-3 py-2 rounded-md text-sm text-neutral-800"
                >
                  <option value="candidat">Candidat</option>
                  <option value="client">Entreprise</option>
                </select>
              </div>
            </>
          )}

          <input
            className="w-full mb-3 p-2 border rounded"
            type="email"
            placeholder="Adresse email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className="relative mb-4">
            <input
              className="w-full p-2 pr-10 border rounded"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {errorMsg && <p className="text-red-500 text-sm mb-3">{errorMsg}</p>}

          <Button onClick={handleAuth} disabled={loading || oauthLoading} className="w-full mb-3">
            {loading ? 'Chargement...' : isSignUp ? 'Créer le compte' : 'Se connecter'}
          </Button>

          <button
            onClick={toggleMode}
            className="text-sm text-primary underline hover:no-underline"
          >
            {isSignUp ? 'Déjà un compte ? Connectez-vous' : "Pas encore de compte ? S'inscrire"}
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
